import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useStateContext } from './contexts/ContextProvider';
import { setLogoutHandler } from './utils/api';
import { Users, Vendors, Services, Content } from './pages';
import AuthLayout from './layouts/AuthLayout';
import Login from './pages/auth/Login';
import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';
import { Navbar, Sidebar } from './components';
import VendorsList from './pages/VendorsList';
import VendorSettings from './pages/VendorSettings';
import Appointments from './pages/Appointments';
import VendorServices from './pages/VendorServices';
import WithdrawalRequests from './pages/WithdrawalRequests';
import TermsAndCondition from './pages/TermsAndCondition';
import Dashboard from './pages/Dashboard';
import Notification from './pages/Notification';
import Finance from './pages/Finance';
import CancellationPolicy from './pages/CancellationPolicy';
import Coupons from './pages/Coupons';
import VendorAddons from './pages/VendorAddons';
import Memberships from './pages/Memberships';
import SupportTickets from './pages/SupportTickets';
import PartnerLeads from './pages/PartnerLeads';

export const App = () => {
  const { activeMenu, setActiveMenu, screenSize, logout } = useStateContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    // Set up the logout handler for API interceptors
    setLogoutHandler(logout);

    const token = localStorage.getItem('token');
    if (!token && !pathname.includes('/login') && !pathname.includes('/register')) {
      navigate("/login");
    }
  }, [navigate, logout]);

  const isAuthPage = pathname === '/register' || pathname === '/login';
  const isMobile = screenSize <= 900;
  // Desktop: full sidebar when open, icon rail when dismissed. Mobile: drawer.
  const railOnly = !activeMenu && !isMobile;
  const sidebarVisible = activeMenu || railOnly;
  // The sidebar's own padding provides the left gutter
  const gutter = isMobile ? 'px-4' : 'pr-6';

  const routes = (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Protected routes with PrivateRoute */}
      <Route path="/dashboard" element={<PrivateRoute element={Dashboard} />} />
      <Route path="/users" element={<PrivateRoute element={Users} />} />
      <Route path="/amenities" element={<PrivateRoute element={Vendors} />} />
      <Route path="/services" element={<PrivateRoute element={Services} />} />
      <Route path="/content" element={<PrivateRoute element={Content} />} />
      <Route path="/terms-and-conditions" element={<PrivateRoute element={TermsAndCondition} />} />
      <Route path="/vendors-list" element={<PrivateRoute element={VendorsList} />} />
      <Route path="/vendor-settings/commission" element={<PrivateRoute element={VendorSettings} />} />
      <Route path="/vendor-settings/vendor-services" element={<PrivateRoute element={VendorServices} />} />
      <Route path="/appointments" element={<PrivateRoute element={Appointments} />} />
      <Route path="/notification" element={<PrivateRoute element={Notification} />} />
      <Route path="/cancellation-policy" element={<PrivateRoute element={CancellationPolicy} />} />
      <Route path="/coupons" element={<PrivateRoute element={Coupons} />} />
      <Route path="/vendor-addons" element={<PrivateRoute element={VendorAddons} />} />
      <Route path="/memberships" element={<PrivateRoute element={Memberships} />} />

      {/* Wallet management */}
      <Route path="/wallet/finance" element={<PrivateRoute element={Finance} />} />
      <Route path="/wallet/withdrawal-requests" element={<PrivateRoute element={WithdrawalRequests} />} />

      {/* Support Tickets */}
      <Route path="/support/tickets" element={<PrivateRoute element={SupportTickets} />} />

      {/* Partner Leads */}
      <Route path="/partner-leads" element={<PrivateRoute element={PartnerLeads} />} />
    </Routes>
  );

  if (isAuthPage) return routes;

  return (
    <div className="min-h-screen bg-main-bg">
      {/* Sidebar: fixed on desktop (icon rail when dismissed), slide-over drawer on mobile */}
      {activeMenu && isMobile && (
        <div className="fixed inset-0 z-30 bg-half-transparent" onClick={() => setActiveMenu(false)} />
      )}
      <div
        className={`fixed inset-y-0 left-0 z-40 p-4 transition-all duration-200 ${railOnly ? 'w-24' : 'w-72'} ${
          sidebarVisible ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar collapsed={railOnly} />
      </div>

      <div className={`min-w-0 transition-[margin] duration-200 ${isMobile ? '' : railOnly ? 'ml-24' : 'ml-72'}`}>
        <div className={`sticky top-0 z-20 pt-4 bg-main-bg/80 backdrop-blur ${gutter}`}>
          <Navbar />
        </div>
        <main className={`py-6 ${gutter}`}>
          <ErrorBoundary resetKey={pathname}>{routes}</ErrorBoundary>
        </main>
      </div>
    </div>
  );
};
